import type { PasswordStrengthProps } from "#ui/password-strength";

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 50;
export const USER_ID_MIN_LENGTH = 6;
export const USER_ID_MAX_LENGTH = 20;
export const FRIENDLY_NAME_MAX_LENGTH = 20;

export interface PasswordPolicy {
  MinLength?: number | null;
  MaxLength?: number | null;
  DisallowedSubstrings?: string[] | null;
  MinUniqueCharacters?: number | null;
  NumberSequenceMaxLength?: number | null;
  ShiftedNumberRowSequenceMaxLength?: number | null;
  QwertySequenceMaxLength?: number | null;
  AlphabetSequenceMaxLength?: number | null;
  MinCharactersRequirements?: Record<string, number> | null;
}

// Don't export `isGte` - internal use only
function isGte(value: string, min: number) {
  return value.length >= min;
}

// Don't export `isLte` - internal use only
function isLte(value: string, max: number) {
  return value.length <= max;
}

export function hasSpaces(value: string) {
  return value.split("").some((el) => /\s/.test(el));
}

function hasOneLowerCase(value: string) {
  return value.split("").some((el) => /[a-z]/.test(el));
}

function hasOneUpperCase(value: string) {
  return value.split("").some((el) => /[A-Z]/.test(el));
}

function hasOneNumber(value: string) {
  return value.split("").some((el) => /[0-9]/.test(el));
}

function hasOneSymbol(value: string) {
  return value
    .split("")
    .some((el) => /[~`!@#$%^&*()_\-+={}[\]\\|;:'",.<>?/]/.test(el));
}

function hasPasswordRequiredCriteria(value: string) {
  return (
    [
      hasOneLowerCase(value),
      hasOneUpperCase(value),
      hasOneNumber(value),
      hasOneSymbol(value),
    ].filter(Boolean).length >= 4
  );
}

function containsDisallowedSubstring(value: string, disallowed: string[]) {
  const lowered = value.toLowerCase();
  return disallowed.some((bad) => {
    if (!bad || !bad.trim()) return false;
    return lowered.includes(bad.toLowerCase());
  });
}

function hasMinUniqueCharacters(value: string, minUnique: number) {
  return new Set(value.split("")).size >= minUnique;
}

// number sequence rule (simple ascending sequences 0123 or 1234)
function hasAscendingNumericSequence(input: string, minRunLength: number) {
  let run = 1;
  for (let i = 1; i < input.length; i += 1) {
    const prev = input.charAt(i - 1);
    const curr = input.charAt(i);
    if (/\d/.test(prev) && /\d/.test(curr)) {
      if (curr.charCodeAt(0) - prev.charCodeAt(0) === 1) {
        run += 1;
        if (run >= minRunLength) return true;
      } else {
        run = 1;
      }
    } else {
      run = 1;
    }
  }
  return false;
}

// shifted number row symbol sequences: common US keyboard shifted symbols !@#$%^&*()
function hasSequentialSubstring(
  input: string,
  sequenceSource: string,
  minRunLength: number,
) {
  // Check forward runs only for simplicity
  for (let start = 0; start < sequenceSource.length; start += 1) {
    const maxLen = sequenceSource.length - start;
    if (maxLen < minRunLength) break;
    const candidate = sequenceSource.slice(start);
    for (let len = minRunLength; len <= candidate.length; len += 1) {
      const sub = candidate.slice(0, len);
      if (input.includes(sub)) return true;
    }
  }
  return false;
}

// alphabet sequence rule: simple A..Z or a..z ascending runs
function hasAscendingAlphabeticSequence(input: string, minRunLength: number) {
  let run = 1;
  for (let i = 1; i < input.length; i += 1) {
    let prev = input.charAt(i - 1);
    let curr = input.charAt(i);
    if (/[a-zA-Z]/.test(prev) && /[a-zA-Z]/.test(curr)) {
      prev = prev.toLowerCase();
      curr = curr.toLowerCase();
      if (curr.charCodeAt(0) - prev.charCodeAt(0) === 1) {
        run += 1;
        if (run >= minRunLength) return true;
      } else {
        run = 1;
      }
    } else {
      run = 1;
    }
  }
  return false;
}

function countFromCharset(value: string, charset: string) {
  let count = 0;
  for (const c of value) {
    if (charset.includes(c)) count += 1;
  }
  return count;
}

function getKeyValueNumberFromPasswordPolicy(
  policy: Record<string, unknown> | null | undefined,
  pascalKey: string,
  camelKey: string,
): number | undefined {
  const raw = (policy?.[pascalKey] ?? policy?.[camelKey]) as unknown;
  return typeof raw === "number" && Number.isFinite(raw) ? raw : undefined;
}

function getKeyValueStringArrayFromPasswordPolicy(
  policy: Record<string, unknown> | null | undefined,
  pascalKey: string,
  camelKey: string,
): string[] | undefined {
  const raw = (policy?.[pascalKey] ?? policy?.[camelKey]) as unknown;
  if (!Array.isArray(raw)) return undefined;
  return raw.filter((el): el is string => typeof el === "string");
}

function getPolicyRecordNumber(
  policy: Record<string, unknown> | null | undefined,
  pascalKey: string,
  camelKey: string,
): Record<string, number> | undefined {
  const raw = (policy?.[pascalKey] ?? policy?.[camelKey]) as unknown;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const record = raw as Record<string, unknown>;
  const entries = Object.entries(record).filter(
    (el): el is [string, number] => typeof el[1] === "number" && el[1] > 0,
  );
  return entries.length > 0
    ? Object.fromEntries(entries) // preserves keys in insertion order
    : undefined;
}

export function hasPasswordCriteria(value: string) {
  return (
    isGte(value, PASSWORD_MIN_LENGTH) && hasPasswordRequiredCriteria(value)
  );
}

export function getPasswordStrengthOrMeterValue(
  value: string,
): PasswordStrengthProps["value"] {
  return hasPasswordRequiredCriteria(value)
    ? 3
    : isGte(value, PASSWORD_MIN_LENGTH)
      ? 2
      : 1;
}

export function getPasswordFieldRequirementsPropsAuthVendor(
  value: string,
  policy?:
    | PasswordPolicy
    | (Record<string, unknown> & Partial<PasswordPolicy>)
    | null,
) {
  const policyAny = policy as Record<string, unknown> | null | undefined;

  const minLength = getKeyValueNumberFromPasswordPolicy(
    policyAny,
    "MinLength",
    "minLength",
  );

  // Note:  Not using Auth Vendor's actual maximum (255).  Sticking with 50 because tblLogin.Password hash can't handle more than 50 characters.
  // When legacy password is fully removed, could increase this maximum.
  const maxLength = PASSWORD_MAX_LENGTH;

  const disallowed = getKeyValueStringArrayFromPasswordPolicy(
    policyAny,
    "DisallowedSubstrings",
    "disallowedSubstrings",
  );
  const minUnique = getKeyValueNumberFromPasswordPolicy(
    policyAny,
    "MinUniqueCharacters",
    "minUniqueCharacters",
  );
  const numberSeqMax = getKeyValueNumberFromPasswordPolicy(
    policyAny,
    "NumberSequenceMaxLength",
    "numberSequenceMaxLength",
  );
  const shiftedSeqMax = getKeyValueNumberFromPasswordPolicy(
    policyAny,
    "ShiftedNumberRowSequenceMaxLength",
    "shiftedNumberRowSequenceMaxLength",
  );
  const qwertySeqMax = getKeyValueNumberFromPasswordPolicy(
    policyAny,
    "QwertySequenceMaxLength",
    "qwertySequenceMaxLength",
  );
  const alphabetSeqMax = getKeyValueNumberFromPasswordPolicy(
    policyAny,
    "AlphabetSequenceMaxLength",
    "alphabetSequenceMaxLength",
  );
  const minCharsReq = getPolicyRecordNumber(
    policyAny,
    "MinCharactersRequirements",
    "minCharactersRequirements",
  );

  const items: {
    label: string;
    checked: boolean | null;
    items?: { label: string; checked: boolean }[];
  }[] = [];

  if (typeof minLength === "number") {
    items.push({
      label: `Must be at least ${minLength} characters`,
      checked: isGte(value, minLength),
    });
  }

  if (typeof maxLength === "number") {
    items.push({
      label: `Must be no more than ${maxLength} characters`,
      checked: isLte(value, maxLength),
    });
  }

  if (disallowed && disallowed.length > 0) {
    items.push({
      label: "Must not contain disallowed text",
      checked: !containsDisallowedSubstring(value, disallowed),
    });
  }

  if (typeof minUnique === "number") {
    items.push({
      label: `Must contain at least ${minUnique} unique characters`,
      checked: hasMinUniqueCharacters(value, minUnique),
    });
  }

  if (typeof numberSeqMax === "number") {
    items.push({
      label: `Must not contain a numeric sequence longer than allowed (${numberSeqMax})`,
      checked: !hasAscendingNumericSequence(value, numberSeqMax + 1),
    });
  }

  if (typeof shiftedSeqMax === "number") {
    const shifted = "!@#$%^&*()";
    items.push({
      label: `Must not contain a shifted number row sequence longer than allowed (${shiftedSeqMax})`,
      checked: !hasSequentialSubstring(value, shifted, shiftedSeqMax + 1),
    });
  }

  if (typeof qwertySeqMax === "number") {
    const rows = ["qwertyuiop", "asdfghjkl", "zxcvbnm"];
    const lowered = value.toLowerCase();
    const hasQwerty = rows.some((row) =>
      hasSequentialSubstring(lowered, row, qwertySeqMax + 1),
    );

    items.push({
      label: `Must not contain a keyboard sequence longer than allowed (${qwertySeqMax})`,
      checked: !hasQwerty,
    });
  }

  if (typeof alphabetSeqMax === "number") {
    items.push({
      label: `Must not contain an alphabetic sequence longer than allowed (${alphabetSeqMax})`,
      checked: !hasAscendingAlphabeticSequence(value, alphabetSeqMax + 1),
    });
  }

  if (minCharsReq && Object.keys(minCharsReq).length > 0) {
    const nested = Object.entries(minCharsReq).map(([charset, required]) => ({
      label: `Must include at least ${required} ${_charsetType(charset)}`,
      checked: countFromCharset(value, charset) >= required,
    }));

    items.push({
      label: "Must meet character requirements:",
      checked: nested.every((el) => el.checked),
      items: nested,
    });
  }
  items.push({
    label: `Must not contain a common password`,
    checked: null,
  });

  return {
    label: "Your password must have:",
    items,
  };
}

function _charsetType(value: string) {
  let type = "";

  switch (value) {
    case "~!@#$%^&*()-_=+[]{}|;:,.<>/?":
      type = "symbol(s)";
      break;
    case "0123456789":
      type = "number(s)";
      break;
    case "ABCDEFGHIJKLMNOPQRSTUVWXYZ":
      type = "capital letter(s)";
      break;
    case "abcdefghijklmnopqrstuvwxyz":
      type = "lower case letter(s)";
      break;
    default:
      type = value;
  }
  return type;
}

export function getUserIdFieldRequirementsProps(value: string) {
  return {
    label: "User ID Requirements:",
    items: [
      {
        label: `Between ${USER_ID_MIN_LENGTH} and ${USER_ID_MAX_LENGTH} characters`,
        checked:
          isGte(value, USER_ID_MIN_LENGTH) && isLte(value, USER_ID_MAX_LENGTH),
      },
      {
        label: "Can be letters, numbers and/or symbols, in any combination",
        checked: isGte(value, 1),
      },
      {
        label: "May not contain spaces",
        checked: isGte(value, 2) && !hasSpaces(value),
      },
    ],
  };
}

/**
 * Changes the format from `"MM/dd/yyyy"` to `"yyyy-MM-dd"`
 * which `z.date` expects.
 */
export function transformDatePickerValue(value: string): string {
  const [MM, dd, yyyy] = value.split("/");
  return [yyyy, MM, dd].join("-");
}
